import React, { useContext, useMemo, useState } from 'react';

import {
  TestimonialAssetPage,
  TestimonialRespondentsMedia as RespondentMedia,
  TestimonialResponseType,
} from '~/types/models/testimonial';
import ActionLinks, { ActionLink } from './ActionLinks';
import DeleteConfirmationModal from '~/App/Components/Modals/DeleteConfirmationModal';
import ThumbnailPickerModal from '../../Testimonials/Components/ThumbnailPicker/ThumbnailPickerModal';
import { ThumbnailArgs } from '~/App/Components/Record/Stores/Recorder/Thumbnail';
import { getThumbnailBlob } from '~/utils/thumbnail';
import {
  SaveResponseParams,
  useDeleteResponse,
  useUpdateResponse,
} from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';
import cx from 'classnames';
import {
  EditableThumbnailResourcePage,
  EditableThumbnailResourceType,
} from '~/types/models/thumbnail';
import { useRouteMatch } from 'react-router';
import { useQueryClient } from 'react-query';
import { testimonialRespondentsMediaQueryKeys } from '../Api/Testimonials';
import {
  TestimonialAssetEditorModelType,
  openTestimonialAssetEditorModal,
} from '../../Testimonials/Components/TestimonialAssetEditorModal';
import FlashContext from '~/App/Components/Flash/Context';
import useResourceTagging from '../../Tags/hooks/useResourceTagging';
import { startUpgradeModalFlow } from '~/App/Components/Modals/UpgradeModalFlow';
import { UpgradeModalTrigger } from '~/App/Components/Modals/UpgradeModalFlow/util';

export interface ResponseActionLinksProps {
  response: RespondentMedia;
  onActionSuccess: () => void;
}

export default function ResponseActionLinks({
  response,
  onActionSuccess,
}: ResponseActionLinksProps) {
  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);
  const { defaultTags } = useResourceTagging(response);
  const locked = !!response.is_locked;

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showThumbnailPickerModal, setShowThumbnailPickerModal] =
    useState(false);

  const closeThumbnailPickerModal = () => setShowThumbnailPickerModal(false);
  const closeDeleteConfirmationModal = () =>
    setShowDeleteConfirmationModal(false);

  const match = useRouteMatch<{ assetPage?: TestimonialAssetPage }>();
  const { assetPage } = match.params;

  const editableThumbnailResourcePage = useMemo(() => {
    switch (assetPage) {
      case TestimonialAssetPage.All:
        return EditableThumbnailResourcePage.TestimonialAll;
      case TestimonialAssetPage.Responses:
        return EditableThumbnailResourcePage.TestimonialResponses;
      case TestimonialAssetPage.Uploads:
      default:
        return EditableThumbnailResourcePage.TestimonialUploads;
    }
  }, [assetPage]);

  const invalidateQuery = () => {
    void queryClient.invalidateQueries(
      testimonialRespondentsMediaQueryKeys.all
    );
  };

  const { mutateAsync: updateResponseMutation, isLoading: updating } =
    useUpdateResponse({
      options: {
        onSuccess: () => {
          closeThumbnailPickerModal();
          onActionSuccess();
          invalidateQuery();
        },
      },
    });

  const { mutateAsync: deleteResponseMutation, isLoading: deleting } =
    useDeleteResponse({
      options: {
        onSuccess: () => {
          closeDeleteConfirmationModal();
          onActionSuccess();
          invalidateQuery();
        },
      },
    });

  const handleOnThumbnailSelected = async (thumbnail: ThumbnailArgs) => {
    const params = {
      respondentId: response.respondent.uuid,
      questionId: response.question_uuid,
      responseId: response.uuid,
      type: TestimonialResponseType.Video,
      thumbnail: await getThumbnailBlob(thumbnail.small),
      thumbnailFull: await getThumbnailBlob(thumbnail.original),
    } as SaveResponseParams;
    await updateResponseMutation(params);
  };

  const handleOnDelete = () => {
    void deleteResponseMutation({ responseUuid: response.uuid });
  };

  const defaultLinkClass = cx('hover:!v2-bg-yellow-100');

  const deleteButtonClass = cx('v2-text-orange-700');

  const deleteButtonContainerClass = cx('hover:!v2-bg-magenta-50');

  const promptTestimonialUpgradeModal = () => {
    if (locked) {
      startUpgradeModalFlow({
        trigger: UpgradeModalTrigger.GenericTestimonialsUpgrade,
      });
      return true;
    }

    return false;
  };
  const onEditClick = () => {
    if (promptTestimonialUpgradeModal()) return;

    openTestimonialAssetEditorModal({
      type: TestimonialAssetEditorModelType.TestimonialResponse,
      title: 'Edit response',
      id: response.uuid,
      profile: response.respondent?.profile,
      videoUrl: response.video_url,
      text: response.text_response_rich,
      transcript: response.transcript,
      modelProperties: {
        respondentId: response.respondent?.uuid,
        questionId: response?.question_uuid,
        responseId: response?.uuid,
        testimonialUuid: response?.testimonial?.uuid,
      },
      tags: defaultTags,
      successCallback: () => {
        createFlash({
          id: 'testimonial-asset-edit-update-success',
          title: 'Success',
          message: 'Response updated!',
        });
      },
    });
  };

  const onEditThumbnailClick = () => {
    if (promptTestimonialUpgradeModal()) return;
    setShowThumbnailPickerModal(true);
  };

  const links = useMemo<ActionLink[]>(() => {
    return [
      ...(response?.video_url != null
        ? [
            {
              label: (
                <>
                  <i className='fa fa-image' />{' '}
                  <span className='text-sm'>Edit thumbnail</span>
                </>
              ),
              onClick: onEditThumbnailClick,
              className: defaultLinkClass,
            },
          ]
        : []),
      {
        label: (
          <>
            <i className='fa fa-edit' /> <span className='text-sm'>Edit</span>
          </>
        ),
        onClick: onEditClick,
        className: deleteButtonContainerClass,
      },
      {
        label: (
          <>
            <i className={`fa fa-trash ${deleteButtonClass}`} />{' '}
            <span className={`text-sm  ${deleteButtonClass}`}>Delete</span>
          </>
        ),
        onClick: () => setShowDeleteConfirmationModal(true),
        className: deleteButtonContainerClass,
      },
    ];
  }, [response]);

  return (
    <>
      <ActionLinks links={links} />
      <ThumbnailPickerModal
        open={showThumbnailPickerModal}
        onClose={closeThumbnailPickerModal}
        videoUrl={response.video_url}
        onSave={handleOnThumbnailSelected}
        saving={updating}
        resourcePage={editableThumbnailResourcePage}
        resourceType={EditableThumbnailResourceType.TestimonialResponse}
      />
      <DeleteConfirmationModal
        open={showDeleteConfirmationModal}
        title={`Are you sure you want to delete ${
          response.testimonial.name ?? 'this response'
        }?`}
        onConfirm={handleOnDelete}
        onCancel={closeDeleteConfirmationModal}
        loading={deleting}
      />
    </>
  );
}
